import { useEffect, useRef, useState } from 'react';
import { useSavePerfil } from '../api';
import type { Detail } from '../schemas';

export type PerfilStatus = 'idle' | 'salvando' | 'salvo' | 'erro';

export interface PerfilFields {
  decisor: string;
  velocidade: string;
  sensibilidade: string;
  reacao: string;
  statusRel: string;
  jaRecusou: boolean;
  detalheRecusas: string;
  conhecimento: string;
  canal: string;
  maturidade: string;
}

export interface PerfilSetters {
  setDecisor: (v: string) => void;
  setVelocidade: (v: string) => void;
  setSensibilidade: (v: string) => void;
  setReacao: (v: string) => void;
  setStatusRel: (v: string) => void;
  setJaRecusou: (v: boolean) => void;
  setDetalheRecusas: (v: string) => void;
  setConhecimento: (v: string) => void;
  setCanal: (v: string) => void;
  setMaturidade: (v: string) => void;
}

interface UsePerfilFormReturn {
  fields: PerfilFields;
  setters: PerfilSetters;
  status: PerfilStatus;
  /** Snapshot serializável dos campos pra incluir em outro submit (qualificação). */
  payload: Record<string, unknown>;
}

/**
 * Gerencia o formulário do perfil comportamental:
 *  - pré-preenche do `detail.perfil` quando o cliente muda
 *  - autosave debounced 800ms a cada edição
 *  - status efêmero (salvando → salvo por 1.5s → idle)
 *
 * Centraliza o que antes ficava espalhado em 10 useStates + 2 useEffects no
 * CrmDetailModal. Outras fases (Abordagem) podem ler `fields.canal` pra herdar
 * o canal preferido sem precisar do detail.
 */
export function usePerfilForm(detail: Detail | undefined, clienteCpf: string | null | undefined, cardId: string | null | undefined): UsePerfilFormReturn {
  const [decisor, setDecisor] = useState('');
  const [velocidade, setVelocidade] = useState('');
  const [sensibilidade, setSensibilidade] = useState('');
  const [reacao, setReacao] = useState('');
  const [statusRel, setStatusRel] = useState('');
  const [jaRecusou, setJaRecusou] = useState(false);
  const [detalheRecusas, setDetalheRecusas] = useState('');
  const [conhecimento, setConhecimento] = useState('');
  const [canal, setCanal] = useState('');
  const [maturidade, setMaturidade] = useState('');
  const [status, setStatus] = useState<PerfilStatus>('idle');

  const savePerfil = useSavePerfil(clienteCpf ?? null);
  const perfilCpfRef = useRef<string | null>(null);
  const skipNextAutosaveRef = useRef(true);

  const perfilSalvo = detail?.perfil ?? null;

  // Pula próximo autosave quando o card abre.
  useEffect(() => {
    skipNextAutosaveRef.current = true;
  }, [cardId]);

  // Pré-preenche quando muda de cliente.
  useEffect(() => {
    if (!perfilSalvo) {
      if (perfilCpfRef.current !== (clienteCpf ?? null)) {
        perfilCpfRef.current = clienteCpf ?? null;
        setDecisor('');
        setVelocidade('');
        setSensibilidade('');
        setReacao('');
        setStatusRel('');
        setJaRecusou(false);
        setDetalheRecusas('');
        setConhecimento('');
        setCanal('');
        setMaturidade('');
      }
      return;
    }
    if (perfilCpfRef.current === perfilSalvo.cliente_cpf) return;
    perfilCpfRef.current = perfilSalvo.cliente_cpf;
    setDecisor(perfilSalvo.decisor ?? '');
    setVelocidade(perfilSalvo.velocidade_decisao ?? '');
    setSensibilidade(perfilSalvo.sensibilidade_preco ?? '');
    setReacao(perfilSalvo.reacao_regulatorio ?? '');
    setStatusRel(perfilSalvo.status_relacionamento ?? '');
    setJaRecusou(!!perfilSalvo.ja_recusou_servicos);
    setDetalheRecusas(perfilSalvo.detalhe_recusas ?? '');
    setConhecimento(perfilSalvo.conhecimento_tema ?? '');
    setCanal(perfilSalvo.canal_preferido ?? '');
    setMaturidade(perfilSalvo.maturidade_operacao ?? '');
  }, [perfilSalvo, clienteCpf]);

  // Autosave debounced.
  useEffect(() => {
    if (!clienteCpf) return undefined;
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return undefined;
    }
    setStatus('salvando');
    const t = setTimeout(() => {
      savePerfil.mutate(
        {
          decisor: decisor || null,
          velocidade_decisao: velocidade || null,
          sensibilidade_preco: sensibilidade || null,
          reacao_regulatorio: reacao || null,
          status_relacionamento: statusRel || null,
          ja_recusou_servicos: jaRecusou,
          detalhe_recusas: detalheRecusas.trim() || null,
          conhecimento_tema: conhecimento || null,
          canal_preferido: canal || null,
          maturidade_operacao: maturidade || null,
        },
        {
          onSuccess: () => {
            setStatus('salvo');
            setTimeout(() => setStatus('idle'), 1500);
          },
          onError: () => setStatus('erro'),
        },
      );
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisor, velocidade, sensibilidade, reacao, statusRel, jaRecusou, detalheRecusas, conhecimento, canal, maturidade]);

  const payload: Record<string, unknown> = { ja_recusou_servicos: jaRecusou };
  if (decisor) payload.decisor = decisor;
  if (velocidade) payload.velocidade_decisao = velocidade;
  if (sensibilidade) payload.sensibilidade_preco = sensibilidade;
  if (reacao) payload.reacao_regulatorio = reacao;
  if (statusRel) payload.status_relacionamento = statusRel;
  if (detalheRecusas.trim()) payload.detalhe_recusas = detalheRecusas.trim();
  if (conhecimento) payload.conhecimento_tema = conhecimento;
  if (canal) payload.canal_preferido = canal;
  if (maturidade) payload.maturidade_operacao = maturidade;

  return {
    fields: { decisor, velocidade, sensibilidade, reacao, statusRel, jaRecusou, detalheRecusas, conhecimento, canal, maturidade },
    setters: {
      setDecisor, setVelocidade, setSensibilidade, setReacao, setStatusRel,
      setJaRecusou, setDetalheRecusas, setConhecimento, setCanal, setMaturidade,
    },
    status,
    payload,
  };
}
